import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, Mail, MapPin } from "lucide-react";
import { logoWhite } from "@/lib/cdn-images";

const GIVE_URL = "https://give.abbashouse.org";
const PHONE_DISPLAY = "1-833-707-HOPE";
const PHONE_TEL = "tel:8337074673";
const EMAIL = "hello@abbashouse.org";

const SOCIAL_LINKS = [
  { Icon: Facebook, href: "https://www.facebook.com/ourabbashouse", label: "Facebook" },
  { Icon: Instagram, href: "https://www.instagram.com/ourabbashouse", label: "Instagram" },
];

export default function Footer() {
  return (
    <footer className="relative z-10 bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12 text-center md:text-left">
          {/* Logo & Mission */}
          <div className="flex flex-col items-center md:items-start gap-4">
            <img src={logoWhite} alt="Abba's House Communities" width={200} height={56} className="h-14 w-auto object-contain" loading="lazy" decoding="async" />
            <p className="text-sm opacity-70 leading-relaxed max-w-xs">
              A nonprofit Christian ministry dedicated to ending homelessness — one life at a time.
            </p>
            {/* Phone */}
            <a
              href={PHONE_TEL}
              className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
            >
              <Phone className="h-4 w-4" />
              {PHONE_DISPLAY}
            </a>
            {/* Email */}
            <a
              href={`mailto:${EMAIL}`}
              className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
            >
              <Mail className="h-4 w-4" />
              {EMAIL}
            </a>
            {/* Write to Us — Corporate HQ */}
            <div className="flex items-start gap-2 text-sm opacity-70 mt-1">
              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <address className="not-italic leading-relaxed">
                <span className="block font-semibold opacity-90">Write to us</span>
                <span className="block">101 N. 1st Ave., Suite 2325</span>
                <span className="block">Phoenix, AZ 85003</span>
                <span className="block text-xs opacity-75 mt-1">Corporate office</span>
              </address>
            </div>
            {/* Social Icons */}
            <div className="flex items-center gap-4 mt-1">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="opacity-60 hover:opacity-100 transition-opacity"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-sm font-semibold tracking-widest uppercase opacity-80 mb-4">Quick Links</h3>
            <nav className="flex flex-col gap-3">
            {[
                { label: "About", to: "/about" },
                { label: "Programs", to: "/programs" },
                { label: "Partner", to: "/partner" },
                { label: "FAQ", to: "/faq" },
                { label: "Contact / Apply", to: "/contact" },
                { label: "Transparency", to: "/transparency" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm opacity-90 hover:opacity-100 transition-opacity"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://portal.abbashouse.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm opacity-90 hover:opacity-100 transition-opacity"
              >
                Portal ↗
              </a>
              <a
                href={GIVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm opacity-90 hover:opacity-100 transition-opacity"
              >
                Give Now ↗
              </a>
            </nav>
          </div>

          {/* Scripture */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-sm font-semibold tracking-widest uppercase opacity-80 mb-4">Our Foundation</h3>
            <blockquote className="text-sm opacity-90 leading-relaxed italic border-l-2 border-primary-foreground/30 pl-4">
               "Protect the rights of the poor and the orphans. Be fair to the needy and the helpless. Rescue the poor and helpless. Save them from the power of the wicked."
              <br />
              <span className="not-italic font-semibold opacity-80 mt-1 block">— Psalm 82:3–4 (ERV)</span>
            </blockquote>
            <p className="text-sm font-medium opacity-80 mt-10 tracking-wide">EIN: 41-4644298</p>
            <p className="text-xs opacity-75 mt-1 tracking-wide">Contributions are tax deductible</p>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 pt-8 text-center space-y-2">
          <p className="text-xs opacity-75">
            © 2026 Abba's House Communities Inc. All Rights Reserved.
          </p>
          <p className="text-xs opacity-70 max-w-2xl mx-auto leading-relaxed">
            Abba's House Communities Inc. is an independent ministry and is not affiliated, associated with, or endorsed by Abba's House Inc. in Hixson, Tennessee.
          </p>
        </div>
      </div>
    </footer>
  );
}